import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { checkActiveUser, fetchAllUsers } from '../store/slices/authSlice'
import { useSidebarContext } from '../scenes/global/sidebar/SidebarContext'

const CustomIcon = ({ children }) => {
  const { theme } = useSidebarContext()
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      className={`w-6 h-6 ${theme.tailwind.text9} transform transition-transform group-hover:scale-110 duration-300`}
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      {children}
    </svg>
  )
}

const Icons = {
  Profile: () => (
    <CustomIcon>
      <path d='M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2' />
      <circle cx='12' cy='7' r='4' />
    </CustomIcon>
  ),
  Contact: () => (
    <CustomIcon>
      <path d='M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z' />
    </CustomIcon>
  ),
  Family: () => (
    <CustomIcon>
      <path d='M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2' />
      <circle cx='9' cy='7' r='4' />
      <path d='M23 21v-2a4 4 0 0 0-3-3.87' />
      <path d='M16 3.13a4 4 0 0 1 0 7.75' />
    </CustomIcon>
  ),
  Plot: () => (
    <CustomIcon>
      <path d='M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z' />
      <polyline points='9 22 9 12 15 12 15 22' />
    </CustomIcon>
  ),
  ID: () => (
    <CustomIcon>
      <rect x='3' y='4' width='18' height='16' rx='2' />
      <circle cx='9' cy='10' r='2' />
      <path d='M15 8h2' />
      <path d='M15 12h2' />
      <path d='M7 16h10' />
    </CustomIcon>
  )
}

const SingleCustomer = () => {
  const dispatch = useDispatch()
  const { activeUser, users, loading } = useSelector(state => state.auth)
  const { theme, gradient } = useSidebarContext()

  useEffect(() => {
    dispatch(checkActiveUser()).then(() => {
      dispatch(fetchAllUsers())
    })
  }, [dispatch])

  const matchedUser = users.find(
    user => user._id === activeUser?.id && user.role === 'Customer'
  )

  if (loading) {
    return (
      <div
        className={`min-h-screen ${gradient} flex items-center justify-center`}
      >
        <div
          className={`animate-pulse ${theme.tailwind.text9} text-xl font-bold tracking-wider`}
        >
          Loading...
        </div>
      </div>
    )
  }

  if (!matchedUser) {
    return (
      <div
        className={`min-h-screen ${gradient} flex items-center justify-center`}
      >
        <div
          className={`${theme.tailwind.text9} text-xl font-bold tracking-wider`}
        >
          No active customer found.
        </div>
      </div>
    )
  }

  const { contactInfo, name, plots } = matchedUser.customerData

  return (
    <div className={`min-h-screen ${gradient} py-6 px-4 sm:px-6 lg:px-8`}>
      <div className='max-w-7xl mx-auto space-y-6'>
        {/* Header Section */}
        <div className='mb-6'>
          <h1
            className={`text-3xl md:text-4xl font-bold mb-2 ${theme.tailwind.text9}`}
          >
            My Profile
          </h1>
          <p className={`${theme.tailwind.texting2} text-base`}>
            Your complete account information and property details
          </p>
        </div>

        {/* Main Content Grid - Two Column Layout */}
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
          {/* Left Column - Personal Information */}
          <div className='lg:col-span-2 space-y-6'>
            {/* General Information Card */}
            <Section icon={<Icons.Profile />} title='Personal Information'>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                <InfoItem label='Username' value={matchedUser.username} />
                <InfoItem label='Full Name' value={name} />
              </div>
            </Section>

            {/* Contact Information Card */}
            <Section icon={<Icons.Contact />} title='Contact Information'>
              <div className='space-y-4'>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <InfoItem label='Phone Number' value={contactInfo.phone} />
                  <InfoItem label='Email Address' value={contactInfo.email} />
                </div>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <InfoItem label='CNIC Number' value={contactInfo.cnic} />
                  <InfoItem label='Address' value={contactInfo.address} />
                </div>
              </div>
            </Section>

            {/* Waris Information Card */}
            <Section icon={<Icons.Family />} title='Nominee (Waris) Information'>
              <div className='space-y-4'>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <InfoItem label='Nominee Name' value={contactInfo.waris_name} />
                  <InfoItem label='Relation' value={contactInfo.waris_relation} />
                </div>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <InfoItem label='Nominee Phone' value={contactInfo.waris_phone} />
                  <InfoItem label='Nominee CNIC' value={contactInfo.waris_cnic} />
                </div>
                <div>
                  <InfoItem label='Nominee Address' value={contactInfo.waris_address} />
                </div>
              </div>
            </Section>

            {/* ID Card Images */}
            <Section icon={<Icons.ID />} title='Identity Documents'>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                <ImageCard
                  title='Your ID Card (Front)'
                  src={contactInfo.id_front_img}
                  alt='ID Front'
                />
                <ImageCard
                  title='Your ID Card (Back)'
                  src={contactInfo.id_back_img}
                  alt='ID Back'
                />
                <ImageCard
                  title='Nominee ID Card (Front)'
                  src={contactInfo.waris_id_front_img}
                  alt='Waris ID Front'
                />
                <ImageCard
                  title='Nominee ID Card (Back)'
                  src={contactInfo.waris_id_back_img}
                  alt='Waris ID Back'
                />
              </div>
            </Section>
          </div>

          {/* Right Column - Property Information */}
          <div className='lg:col-span-1'>
            <Section icon={<Icons.Plot />} title='My Properties'>
              {plots && plots.length > 0 ? (
                <div className='space-y-4'>
                  {plots.map((plot, index) => (
                    <PlotCard key={index} plot={plot} />
                  ))}
                </div>
              ) : (
                <div className={`${theme.tailwind.bg60} rounded-xl p-8 text-center border ${theme.tailwind.border2}`}>
                  <div className={`${theme.tailwind.texting2} mb-2`}>
                    <Icons.Plot />
                  </div>
                  <p className={`${theme.tailwind.texting2} text-base font-medium`}>
                    No properties assigned
                  </p>
                  <p className={`${theme.tailwind.texting2} text-sm mt-1 opacity-70`}>
                    Your properties will appear here
                  </p>
                </div>
              )}
            </Section>
          </div>
        </div>
      </div>
    </div>
  )
}

const Section = ({ icon, title, children }) => {
  const { theme } = useSidebarContext()

  return (
    <div
      className={`backdrop-blur-md ${theme.tailwind.bg34} rounded-xl p-5 md:p-6 border ${theme.tailwind.border2} shadow-lg`}
    >
      {/* Section Header */}
      <div className='flex items-center gap-3 mb-5 pb-4 border-b border-white/10'>
        <div className={`p-2 rounded-lg ${theme.tailwind.bg60} ${theme.tailwind.border2} border`}>
          {icon}
        </div>
        <h3 className={`text-lg md:text-xl font-bold ${theme.tailwind.text9}`}>
          {title}
        </h3>
      </div>
      
      {/* Section Content */}
      <div>
        {children}
      </div>
    </div>
  )
}

const InfoItem = ({ label, value }) => {
  const { theme } = useSidebarContext()
  return (
    <div className='space-y-1.5'>
      <p
        className={`${theme.tailwind.texting2} text-xs font-medium uppercase tracking-wide`}
      >
        {label}
      </p>
      <p
        className={`${theme.tailwind.text9} font-semibold text-sm md:text-base break-words leading-relaxed`}
      >
        {value || <span className={`${theme.tailwind.texting2} opacity-60`}>Not provided</span>}
      </p>
    </div>
  )
}

const PlotCard = ({ plot }) => {
  const { theme } = useSidebarContext()
  
  // Check if plot is transferred
  const isTransferred = plot.status === 'Transfer' || plot.booking_status === 'Transfer'
  
  // Check if all installments are paid (plot status is 'Sold' typically means fully paid)
  const allInstallmentsPaid = plot.status === 'Sold'
  
  const getStatusBadge = () => {
    // Registry Completed: Only when plot is transferred AND all installments paid
    if (isTransferred && allInstallmentsPaid) {
      return (
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
            <span className="text-xs font-semibold text-green-400 uppercase tracking-wide">Registry Completed</span>
          </div>
          {plot.registry_date && (
            <p className={`${theme.tailwind.texting2} text-xs mt-1`}>
              Date: {new Date(plot.registry_date).toLocaleDateString('en-GB')}
            </p>
          )}
          {plot.booking_reference && (
            <p className={`${theme.tailwind.texting2} text-xs mt-1`}>
              Ref: {plot.booking_reference}
            </p>
          )}
        </div>
      )
    }
    
    // Registry Eligible: All installments paid but NOT transferred
    if (!isTransferred && allInstallmentsPaid) {
      return (
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
            <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wide">Registry Eligible</span>
          </div>
          <p className={`${theme.tailwind.texting2} text-xs mt-1`}>
            All installments paid. Ready for registry transfer.
          </p>
        </div>
      )
    }
    
    // Payment In Progress: Plot is booked but installments not fully paid
    if (plot.status === 'Booked' || plot.booking_status === 'Booked') {
      return (
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
            <span className="text-xs font-semibold text-blue-400 uppercase tracking-wide">Payment In Progress</span>
          </div>
        </div>
      )
    }
    
    // Cancelled
    if (plot.status === 'Available' && plot.booking_status === 'Cancelled') {
      return (
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-red-500"></div>
            <span className="text-xs font-semibold text-red-400 uppercase tracking-wide">Cancelled</span>
          </div>
        </div>
      )
    }
    
    return null
  }

  return (
    <div
      className={`${theme.tailwind.bg60} rounded-xl p-4 md:p-5 border ${theme.tailwind.border2} shadow-md hover:shadow-lg transition-all duration-300`}
    >
      {/* Plot Header */}
      <div className='mb-4 pb-4 border-b border-white/10'>
        <div className='flex items-start justify-between gap-2'>
          <div>
            <h4 className={`${theme.tailwind.text9} font-bold text-base md:text-lg mb-1`}>
              Plot #{plot.plot_number || 'N/A'}
            </h4>
            {plot.block_number && (
              <p className={`${theme.tailwind.texting2} text-xs`}>
                Block {plot.block_number}
              </p>
            )}
          </div>
          {plot.status && (
            <span className={`px-2.5 py-1 rounded-md text-xs font-semibold uppercase tracking-wide ${
              plot.status === 'Sold' ? 'bg-green-500/20 text-green-400' :
              plot.status === 'Booked' ? 'bg-blue-500/20 text-blue-400' :
              'bg-gray-500/20 text-gray-400'
            }`}>
              {plot.status}
            </span>
          )}
        </div>
      </div>

      {/* Plot Details */}
      <div className='space-y-3'>
        <div className='grid grid-cols-2 gap-3'>
          <InfoItem label='Size' value={plot.size} />
          <InfoItem label='Marla' value={plot.marla ? `${plot.marla}` : 'N/A'} />
        </div>
        <InfoItem label='Category' value={plot.category} />
        {getStatusBadge()}
      </div>
    </div>
  )
}

const ImageCard = ({ title, src, alt }) => {
  const { theme } = useSidebarContext()
  return (
    <div className='space-y-3'>
      <h4
        className={`text-sm font-semibold ${theme.tailwind.text9}`}
      >
        {title}
      </h4>
      {src ? (
        <div className='relative overflow-hidden rounded-lg border border-white/10 group cursor-pointer' onClick={() => window.open(src, '_blank')}>
          <img
            src={src}
            alt={alt}
            className='w-full h-40 md:h-48 object-cover transition-transform duration-300 group-hover:scale-105'
          />
          <div className='absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center'>
            <span className={`${theme.tailwind.text9} text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-md`}>
              Click to view full size
            </span>
          </div>
        </div>
      ) : (
        <div
          className={`w-full h-40 md:h-48 rounded-lg ${theme.tailwind.bg2} flex flex-col items-center justify-center ${theme.tailwind.texting2} text-xs border border-dashed border-white/20`}
        >
          <svg className='w-8 h-8 mb-2 opacity-50' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' />
          </svg>
          <span>No image</span>
        </div>
      )}
    </div>
  )
}

export default SingleCustomer
